<script setup lang="ts">
import { computed } from 'vue';

const props = defineProps({
  rating: {
    type: [Number, String],
    required: true,
    validator(value) {
      const num = Number(value);
      return !Number.isNaN(num) && num >= 0 && num <= 10;
    },
  },
  starSize: {
    type: [Number, String],
    default: 20,
  },
  color: {
    type: String,
    default: '#ff9800',
  },
  bgColor: {
    type: String,
    default: '#595956',
  },
});

const normalizedSize = computed(() => {
  const size = props.starSize;
  return Number.isNaN(size) ? size : `${size}px`;
});

const fillWidth = computed(() => {
  const num = Number(props.rating);
  const clamped = Math.min(Math.max(num, 0), 10);
  return `${clamped * 10}%`;
});

const starPath = 'M12 17.27L18.18 21l-1.64-7.03L22 9.24l-7.19-.61L12 2 9.19 8.63 2 9.24l5.46 4.73L5.82 21z';
</script>

<template>
  <div
    class="star-rating"
    :style="{
      '--star-size': normalizedSize,
      '--active-color': color,
      '--inactive-color': bgColor,
    }"
  >
    <div class="stars-layer stars-bg">
      <svg
        v-for="i in 5"
        :key="i"
        class="star-svg"
        viewBox="0 0 24 24"
        preserveAspectRatio="xMidYMid meet"
      >
        <path :d="starPath" />
      </svg>
    </div>

    <div class="stars-layer stars-filled" :style="{ width: fillWidth }">
      <svg
        v-for="i in 5"
        :key="i"
        class="star-svg"
        viewBox="0 0 24 24"
        preserveAspectRatio="xMidYMid meet"
      >
        <path :d="starPath" />
      </svg>
    </div>
  </div>
</template>

<style scoped>
.star-rating {
  position: relative;
  display: inline-flex; /* Keeps container tight to content */
}

.stars-layer {
  display: flex;
  white-space: nowrap;
}

.stars-bg {
  color: var(--inactive-color);
}

.stars-filled {
  position: absolute;
  top: 0;
  left: 0;
  overflow: hidden; /* Clips the stars based on width % */
  color: var(--active-color);
}

/* SVG Styling */
.star-svg {
  width: var(--star-size);
  height: var(--star-size);
  fill: currentColor; /* Inherits color from parent layer */
  flex-shrink: 0; /* Prevents shrinking if container is squeezed */
  display: block; /* Removes weird inline-block spacing */
}
</style>
